// Objective-attainment gauge for the direct-order tracking screen and its home widget (RG-06/RG-07).
//
// This is deliberately NOT kpiService.defineColors: that one is a 3-state scheme (positive /
// warning / negative) driven by a tolerance around 100%, which is the right model for a KPI that
// either meets its objective or doesn't. The spec asks for a 4-band progress scale instead — the
// gauge is meant to read as "how far along am I", so a run-rate at 60% has to look different from
// one at 10%, which the 3-state scheme cannot express.
//
// Bands come straight from the spec (RG-06): 0–25 red, 26–50 orange, 51–75 yellow, 76–100 green.
// They are expressed on the *rounded* percentage so the boundaries land exactly where the spec
// puts them (25 is still red, 26 is already orange).

export const GAUGE_BANDS = [
	{ max: 25, classColor: 'gauge--critical', color: 'error', hex: '#E7003E' },
	{ max: 50, classColor: 'gauge--low', color: 'warning', hex: '#f57c00' },
	{ max: 75, classColor: 'gauge--medium', color: 'gauge-yellow', hex: '#FBC02D' },
	{ max: Infinity, classColor: 'gauge--high', color: 'success', hex: '#4CAF50' },
];

export function gaugeBand(percentage) {
	const rounded = Math.round(Number.isFinite(percentage) ? percentage : 0);
	return GAUGE_BANDS.find((band) => rounded <= band.max);
}

// An objective of 0 (or null/undefined) means "no objective set for this metric" — RG-07 requires
// showing the raw value with a "Sans objectif" mention rather than a gauge, because an empty bar
// would read as a zero attainment, which is a different and wrong statement.
export function buildGauge(value, objective) {
	const hasObjective = Number.isFinite(objective) && objective > 0;
	if (!hasObjective) {
		return { hasObjective: false, percentage: null, displayPercentage: null, band: null, classColor: '', color: 'primary', hex: null };
	}

	const percentage = ((Number(value) || 0) * 100) / objective;
	const band = gaugeBand(percentage);

	return {
		hasObjective: true,
		// Real attainment, kept intact for the label — CL-03 explicitly wants "128 %" shown even
		// though the bar itself stops at 100.
		percentage,
		// What the bar is allowed to fill, capped at 100 (CL-03). Negative attainment (possible on
		// a metric that went net-negative through returns) is floored at 0 so the bar never inverts.
		displayPercentage: Math.min(100, Math.max(0, percentage)),
		band,
		classColor: band.classColor,
		color: band.color,
		hex: band.hex,
	};
}
